import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import mergeClassNames from 'merge-class-names';
import updateInputWidth, { getFontShorthand } from 'update-input-width';

/* eslint-disable jsx-a11y/no-autofocus */

function select(element) {
  if (!element) {
    return;
  }

  requestAnimationFrame(() => element.select());
}

function updateInputWidthOnFontLoad(element) {
  if (!document.fonts) {
    return;
  }

  const font = getFontShorthand(element);

  if (!font) {
    return;
  }

  const isFontLoaded = document.fonts.check(font);

  if (isFontLoaded) {
    return;
  }

  function onLoadingDone() {
    updateInputWidth(element);
  }

  document.fonts.addEventListener('loadingdone', onLoadingDone);
}

export default function Input({
  ariaLabel,
  autoFocus,
  className,
  disabled,
  itemRef,
  max,
  min,
  name,
  nameForClass,
  onChange,
  onKeyDown,
  onKeyUp,
  placeholder = '--',
  required,
  showLeadingZeros,
  step,
  value,
    amPm,
    arrows,
    increaseValue,
    decreaseValue
}) {
  const hasLeadingZero = showLeadingZeros && value !== null && value < 10;
  const shiftAmPm = amPm === 'pm' ? 12 : 0;
  let increasedValue = value !== null ? value + 1 : min;
  let decreasedValue = value !== null ? value - 1 : max;
  const arrowClassValueUpAdit = `date-time-picker-arrow__up__${name}`;
  const arrowClassValueDownAdit = `date-time-picker-arrow__down__${name}`;
  min = amPm === 'am' ? min - 1 : min;

  const ref = React.createRef();
  const upRef = React.createRef();
  const dnRef = React.createRef();

  const alignArrows = (input) => {
    try {
      const width = +input.style.width.slice(0, input.style.width.length - 2);
      const arrowUp = upRef.current;
      const arrowDown = dnRef.current;
      const w = arrowUp.clientWidth;
      arrowUp.style.left = Math.round((width - w) / 2) + 'px';
      arrowDown.style.left = Math.round((width - w) / 2) + 'px';
    } catch(e) {}
  };

  useEffect(() => {
    alignArrows(ref.current);
    if (ref.current) {
      updateInputWidth(ref.current);
      updateInputWidthOnFontLoad(ref.current);
    }

    if (itemRef) {
      itemRef(ref.current, name);
    }
  });


  return (
    <div className='date-time-picker__arrows-container'>
      {/*{hasLeadingZero && <span key="leadingZero" className={`${className}__leadingZero`}> 0 </span>}*/}
      {
        arrows.includes(name) && <span className={'date-time-picker__arrow date-time-picker-arrow__up ' + arrowClassValueUpAdit}
                                       ref={upRef}
                                           onClick={(e) => {
                                             e.preventDefault();
                                             e.stopPropagation();
                                             increaseValue(name, increasedValue + shiftAmPm)
                                           }
                                           }
        >{'>'}</span>
      }
        <input
          key="input"
          aria-label={ariaLabel}
          autoComplete="off"
          autoFocus={autoFocus}
          className={mergeClassNames(
            `${className}__input`,
            `${className}__${nameForClass || name}`,
            hasLeadingZero && `${className}__input--hasLeadingZero`,
          )}
          disabled={disabled}
          max={max}
          min={min}
          name={name}
          onChange={onChange}
          onFocus={event => select(event.target)}
          onKeyDown={e => {
            console.log(e);
            onKeyDown(e)
          }}
          onKeyUp={(event) => {
            updateInputWidth(event.target);

            if (onKeyUp) {
              onKeyUp(event);
            }
          }}
          placeholder={placeholder}
          ref={ref}
          required={required}
          step={step}
          type="number"
          value={value !== null ? value : 0}
        />
      {
        arrows.includes(name) && <span className={'date-time-picker__arrow date-time-picker-arrow__down ' + arrowClassValueDownAdit}
                                       ref={dnRef}
                                       onClick={(e) => {
                                             e.preventDefault();
                                             e.stopPropagation();
                                             decreaseValue(name, decreasedValue + shiftAmPm)
                                            }
                                           }
        >{'<'}</span>
      }
    </div>
  );
}

Input.propTypes = {
  ariaLabel: PropTypes.string,
  className: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  itemRef: PropTypes.func,
  max: PropTypes.number,
  min: PropTypes.number,
  onChange: PropTypes.func,
  onKeyDown: PropTypes.func,
  required: PropTypes.bool,
  showLeadingZeros: PropTypes.bool,
  step: PropTypes.number,
  value: PropTypes.number,
  arrows: PropTypes.arrayOf(PropTypes.string),
  increaseValue: PropTypes.func,
  decreaseValue: PropTypes.func,
  amPm: PropTypes.string
};
